import { z } from "zod";

// ── Overview ──
const OverviewChannelPerformanceSchema = z.object({
  platform: z.string(),
  average_score: z.number(),
});

const OverviewAIInsightSchema = z.object({
  type: z.string(),
  title: z.string(),
  message: z.string(),
});

export const OverviewAIInsightsAPIDataSchema = z.object({
  total_findings: z.number(),
  insights: z.array(OverviewAIInsightSchema),
});

export const OverviewExpensiveProblemSchema = z.object({
  title: z.string(),
  ai_flagged: z.boolean(),
  description: z.string(),
  metrics: z.record(z.union([z.string(), z.number()])).optional(),
  impact: z
    .object({
      instagram_reels_share_of_voice_change: z.string(),
      tiktok_share_of_voice_change: z.string(),
    })
    .optional(),
  actions: z.array(z.object({ type: z.string(), label: z.string() })).optional(),
});

export const OverviewAPIDataSchema = z.object({
  brand_health: z.number().optional(),
  creative_velocity: z.number().optional(),
  competitive_position: z.number().optional(),
  audience_alignment: z.number().optional(),
  channel_performance: z.array(OverviewChannelPerformanceSchema),
});

// ── Industry ──
export const IndustryStatSchema = z.object({
  label: z.string(),
  value: z.string(),
  sub_label: z.string(),
});

const KeyIndustryShiftSchema = z.object({
  title: z.string(),
  description: z.string(),
});

const IndustryOverviewStatValueSchema = z.object({
  value: z.string(),
  period: z.string(),
});

const IndustryOverviewVelocityItemSchema = z.object({
  year: z.string(),
  assets_per_month: z.number(),
  label: z.string(),
});

export const IndustryOverviewAPIDataSchema = z.object({
  brand_name: z.string(),
  global_market: IndustryOverviewStatValueSchema.optional(),
  yoy_growth: IndustryOverviewStatValueSchema.optional(),
  category_digital_ad_spend: IndustryOverviewStatValueSchema.optional(),
  content_velocity_trend: z.array(IndustryOverviewVelocityItemSchema).optional(),
  key_industry_shifts: z.array(KeyIndustryShiftSchema).optional(),
});

const ShareOfVoiceEntrySchema = z.object({
  brand_name: z.string(),
  absolute_value: z.number(),
  relative_value: z.number(),
});

export const ShareOfVoiceAPIDataSchema = z.object({
  entries: z.array(ShareOfVoiceEntrySchema),
  brand_count: z.number(),
});

// ── Brand Audit ──
export const SubBrandVoiceSchema = z.object({
  name: z.string(),
  tone: z.string().optional(),
  alignment: z.number(),
});

const AIRecommendationSchema = z.object({
  priority: z.string(),
  title: z.string(),
  description: z.string(),
  points: z.number(),
});

const CrossChannelItemSchema = z.object({
  platform: z.string(),
  average_score: z.number(),
});

const SubBrandMappingItemSchema = z.object({
  name: z.string(),
  average_score: z.number(),
});

export const BrandAuditAIRecommendationsAPIDataSchema = z.object({
  brand_name: z.string(),
  total_actions: z.number(),
  recommendations: z.array(AIRecommendationSchema),
});

export const CreativeScoresAPIDataSchema = z.object({
  brand_name: z.string(),
  total_creatives_analyzed: z.number(),
  median_scores: z.record(z.string(), z.number()),
  average_scores: z.record(z.string(), z.union([z.number(), z.string()])),
  cross_channel: z.array(CrossChannelItemSchema).optional(),
  subbrand_mapping: z.array(SubBrandMappingItemSchema).optional(),
});

// ── Creative Audit ──
const APIPlatformDistributionSchema = z.object({
  ads_count: z.number(),
  platform: z.string(),
});

const MediaTypeDistributionSchema = z.object({
  media_type: z.string(),
  count: z.number(),
  percentage: z.number(),
});

export const CreativeAuditAPIDataSchema = z.object({
  brand_name: z.string(),
  total_active_ads: z.number(),
  total_creatives: z.number(),
  video_count: z.number(),
  static_count: z.number(),
  text_count: z.number(),
  video_static_text_ratio: z.string(),
  video_percentage: z.number(),
  static_percentage: z.number(),
  text_percentage: z.number(),
  platform_distribution: z.array(APIPlatformDistributionSchema),
  average_technical_specs: z.unknown().nullable(),
  media_type_distribution: z.array(MediaTypeDistributionSchema).optional(),
});

// ── Competitive  ──
const CriticalGapSchema = z.object({
  title: z.string(),
  description: z.string(),
  icon: z.string().optional(),
  category: z.string().optional(),
});

export const CompetitorPlatformComparisonAPIDataSchema = z.object({
  platform_comparison: z.record(z.string(), z.array(z.record(z.string(), z.number()))).optional(),
  critical_gaps: z
    .object({
      ai_flagged: z.boolean().optional(),
      gaps: z.array(CriticalGapSchema).optional(),
    })
    .optional(),
});

// ── Audience ──

const PlatformAffinityItemSchema = z.object({
  platform: z.string(),
  absolute_minutes_spent: z.number(),
  relative_percentage: z.number().optional(),
});

export const TargetAudienceAPIDataSchema = z.object({
  brand_name: z.string(),
  target_audience: z
    .object({
      brand_name: z.string(),
      total_records: z.number(),
      age_distribution: z.array(z.object({ age_range: z.string(), count: z.number(), percentage: z.number() })).optional(),
    })
    .optional(),
  content_preferences: z.array(z.object({ content_name: z.string(), demand_percentage: z.number() })).optional(),
  platform_affinity: z.array(PlatformAffinityItemSchema).optional(),
});

// ── Content Alignment ──

const ContentAlignmentGapItemSchema = z.object({
  content_name: z.string(),
  audience_demand_percentage: z.number(),
  brand_supply_percentage: z.number(),
  gap: z.number(),
  priority: z.string(),
});

const ContentAlignmentRecommendationsSchema = z.object({
  audience_demand: z.array(z.string()),
  brand_production: z.array(z.string()),
  opportunity: z.array(z.string()),
});

export const ContentAlignmentAPIDataSchema = z.object({
  brand_name: z.string(),
  industry: z.string(),
  total_content_types: z.number(),
  alignment_gaps: z.array(ContentAlignmentGapItemSchema),
  recommendations: ContentAlignmentRecommendationsSchema,
});

export type IndustryStat = z.infer<typeof IndustryStatSchema>;
export type KeyIndustryShift = z.infer<typeof KeyIndustryShiftSchema>;
export type SubBrandVoice = z.infer<typeof SubBrandVoiceSchema>;
export type AIRecommendation = z.infer<typeof AIRecommendationSchema>;
export type CriticalGap = z.infer<typeof CriticalGapSchema>;
export type ChannelPerformanceItem = z.infer<typeof OverviewChannelPerformanceSchema>;
export type ExpensiveProblem = z.infer<typeof OverviewExpensiveProblemSchema>;
export type CreativeAuditAPIData = z.infer<typeof CreativeAuditAPIDataSchema>;
export type IndustryOverviewAPIData = z.infer<typeof IndustryOverviewAPIDataSchema>;
export type BrandAuditAIRecommendationsAPIData = z.infer<typeof BrandAuditAIRecommendationsAPIDataSchema>;
export type CreativeScoresAPIData = z.infer<typeof CreativeScoresAPIDataSchema>;
export type TargetAudienceAPIData = z.infer<typeof TargetAudienceAPIDataSchema>;
export type CompetitorPlatformComparisonAPIData = z.infer<typeof CompetitorPlatformComparisonAPIDataSchema>;
export type OverviewAPIData = z.infer<typeof OverviewAPIDataSchema>;
export type ContentAlignmentAPIData = z.infer<typeof ContentAlignmentAPIDataSchema>;
export type ShareOfVoiceAPIData = z.infer<typeof ShareOfVoiceAPIDataSchema>;
